import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Toast from "../../components/Toast";
import axios from "axios";
import { motion } from "framer-motion";
import FormField from "../../components/FormField";
import { API_BASE_URL } from "../../config";
import useTitle from "../../hooks/useTitle";
import MessagesPanel from "../Messages";

export default function MyProfile() {
  useTitle("Astrologer My Profile | Astro Portal");
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");
  const [bookings, setBookings] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [initialChat, setInitialChat] = useState(null);
  const navigate = useNavigate();

  // const handleLogout = () => {
  //   sessionStorage.removeItem("authSession");
  //   navigate("/login");
  // };
  // ✅ Load user from API on mount
  useEffect(() => {
    const authUser = JSON.parse(sessionStorage.getItem("authSession"));
    if (!authUser) {
      navigate("/astrologer/login");
      return;
    }

    fetchProfile(authUser.user_id);
  }, [navigate]);

  useEffect(() => {
    if (location.state?.tab) {
      setActiveTab(location.state.tab);
    } else {
      setActiveTab("profile");
    }

    if (location.state?.openChat) {
      setInitialChat(location.state.openChat);
    }
  }, [location.state]);

  const fetchProfile = async (id) => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}/auth/get-data`, {
        type: "astrologer",
        id: String(id),
      });

      if (data.status) {
        setUser(data.data);
      } else {
        setToast({
          message: data.message || "Failed to fetch profile",
          type: "danger",
        });
      }
    } catch (err) {
      setToast({ message: "Error fetching profile!", type: "danger" });
    }
  };

  // ✅ Handle form changes
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "profile_photo" && files.length > 0) {
      setUser({ ...user, profile_photo: files[0] }); // store File
    } else {
      setUser({ ...user, [name]: value });
    }
  };

  // ✅ Save changes via API
  const handleSave = async () => {
    if (!user) return;

    const formData = new FormData();
    Object.entries(user).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/astrologers/update`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (data.status) {
        setUser(data.data);
        sessionStorage.setItem("authSession", JSON.stringify(data.data));
        setToast({ message: "Profile updated successfully!", type: "success" });
        setEditMode(false);
      } else {
        setToast({ message: data.message || "Update failed!", type: "danger" });
      }
    } catch (err) {
      setToast({ message: "Error saving profile!", type: "danger" });
    } finally {
      setLoading(false);
    }
  };
  // ✅ Change password API call
  const handleChangePassword = async () => {
    if (
      !user.current_password ||
      !user.new_password ||
      !user.new_password_confirmation
    ) {
      setToast({ message: "All fields are required", type: "warning" });
      return;
    }

    if (user.new_password !== user.new_password_confirmation) {
      setToast({
        message: "New password and confirmation do not match",
        type: "danger",
      });
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${API_BASE_URL}/astrologers/change-password`,
        {
          id: user.id, // 👈 taken from profile data
          current_password: user.current_password,
          new_password: user.new_password,
          new_password_confirmation: user.new_password_confirmation,
        }
      );

      if (data.status) {
        setToast({
          message: data.message || "Password changed successfully!",
          type: "success",
        });

        // ✅ clear password fields from state (but keep profile data intact)
        setUser({
          ...user,
          current_password: "",
          new_password: "",
          new_password_confirmation: "",
        });
      } else {
        setToast({
          message: data.message || "Failed to change password!",
          type: "danger",
        });
      }
    } catch (err) {
      setToast({
        message: err.response?.data?.message || "Error changing password!",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="container-fluid my-5">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="row">
        {/* Sidebar */}
        <div className="col-md-3 mb-4">
          <div
            className="p-3 shadow-sm rounded-4"
            style={{
              background: "#f0f0f3",
              boxShadow: "8px 8px 16px #bebebe, -8px -8px 16px #ffffff",
            }}
          >
            {["profile", "transactions", "bookings", "messages", "settings"].map((tab) => (
              <motion.button
                key={tab}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className={`w-100 text-start fw-semibold mb-2 px-3 py-2 rounded-3 border-0 ${activeTab === tab ? "active-tab" : "text-dark"
                  }`}
                style={{
                  background: activeTab === tab ? "#e0e0e0" : "#f0f0f3",
                  // boxShadow:
                  //   activeTab === tab
                  //     ? "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff"
                  //     : "4px 4px 8px #c9c9c9, -4px -4px 8px #ffffff",
                  transition: "all 0.3s ease",
                }}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "profile" && "👤 My Profile"}
                {tab === "transactions" && "💳 My Transactions"}
                {tab === "bookings" && "📅 My Bookings"}
                {tab === "messages" && "📩 My Messages"}
                {tab === "settings" && "⚙️ Settings"}
              </motion.button>
            ))}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
              className="w-100 text-start fw-semibold px-3 py-2 rounded-3 border-0 text-danger mt-2"
              style={{
                background: "#f0f0f3",
                // boxShadow: "4px 4px 8px #c9c9c9, -4px -4px 8px #ffffff",
                transition: "all 0.3s ease",
              }}
            // onClick={handleLogout}
            >
              🚪 Logout
            </motion.button>
          </div>
        </div>

        {/* Content Area */}
        <div className="col-md-9">
          {/* Profile */}
          {activeTab === "profile" && (
            <div
              className="card border-0 rounded-4 p-4 shadow-sm"
              style={{
                background: "#f0f0f3",
                boxShadow: "8px 8px 16px #bebebe, -8px -8px 16px #ffffff",
              }}
            >
              {/* Profile Header */}
              <div className="d-flex align-items-center mb-4">
                <img
                  src={
                    user.profile_photo
                      ? typeof user.profile_photo === "string"
                        ? user.profile_photo
                        : URL.createObjectURL(user.profile_photo)
                      : "https://randomuser.me/api/portraits/women/65.jpg"
                  }
                  alt="Profile"
                  className="rounded-circle border"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    boxShadow:
                      "inset 4px 4px 8px #c9c9c9, inset -4px -4px 8px #ffffff",
                  }}
                />
                <div className="ms-3">
                  <h4 className="fw-bold mb-1 text-primary">
                    {user.first_name} {user.last_name}
                  </h4>
                  <p className="text-muted mb-0 small">{user.email}</p>
                  <p className="text-muted small">{user.phone_number}</p>
                </div>
                <div className="ms-auto">
                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95, y: 2 }}
                    className="btn fw-bold px-4 rounded-pill"
                    style={{
                      background: "linear-gradient(145deg, #4facfe, #00f2fe)",
                      color: "white",
                      boxShadow: "6px 6px 12px #c9c9c9, -6px -6px 12px #ffffff",
                    }}
                    onClick={() => setEditMode(true)}
                  >
                    Edit Profile
                  </motion.button>
                </div>
              </div>

              {/* Edit Form */}
              {editMode && (
                <form>
                  <div className="row g-3">
                    <FormField
                      label="First Name"
                      name="first_name"
                      value={user.first_name}
                      onChange={handleChange}
                      placeholder="First Name"
                    />
                    <FormField
                      label="Last Name"
                      name="last_name"
                      value={user.last_name}
                      onChange={handleChange}
                      placeholder="Last Name"
                    />
                    <FormField
                      label="Email"
                      name="email"
                      value={user.email}
                      onChange={handleChange}
                      placeholder="Email"
                      type="email"
                    />
                    <FormField
                      label="Phone Number"
                      name="phone_number"
                      value={user.phone_number}
                      onChange={handleChange}
                      placeholder="Phone Number"
                      type="tel"
                    />
                    <FormField
                      label="City"
                      name="city"
                      value={user.city}
                      onChange={handleChange}
                      placeholder="City"
                    />
                    <FormField
                      label="Gender"
                      name="gender"
                      value={user.gender}
                      onChange={handleChange}
                      options={[
                        { value: "male", label: "Male" },
                        { value: "female", label: "Female" },
                        { value: "other", label: "Other" },
                      ]}
                    />

                    <FormField
                      label="Address Line 1"
                      name="address_line_1"
                      value={user.address_line_1}
                      onChange={handleChange}
                      placeholder="Address Line 1"
                    />
                    <FormField
                      label="Address Line 2"
                      name="address_line_2"
                      value={user.address_line_2}
                      onChange={handleChange}
                      placeholder="Address Line 2"
                    />
                    <FormField
                      label="Pincode"
                      name="pincode"
                      value={user.pincode}
                      onChange={handleChange}
                      placeholder="Pincode"
                    />
                    <FormField
                      label="City"
                      name="city"
                      value={user.city}
                      onChange={handleChange}
                      placeholder="City"
                    />
                    <FormField
                      label="State"
                      name="state"
                      value={user.state}
                      onChange={handleChange}
                      placeholder="State"
                    />
                    <FormField
                      label="Country"
                      name="country"
                      value={user.country}
                      onChange={handleChange}
                      placeholder="Country"
                    />
                    <FormField
                      label="Amount"
                      name="amount"
                      value={user.amount}
                      onChange={handleChange}
                      placeholder="Amount"
                    />
                    <div className="col-md-6">
                      <label className="form-label fw-semibold text-secondary">
                        Profile Photo
                      </label>
                      <motion.input
                        whileFocus={{ scale: 1.02 }}
                        type="file"
                        name="profile_photo"
                        accept="image/*"
                        onChange={handleChange}
                        className="form-control form-control-lg rounded-pill shadow-sm fs-6"
                      />
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.05, y: -2 }}
                    whileTap={{ scale: 0.95, y: 2 }}
                    type="button"
                    disabled={loading}
                    onClick={handleSave}
                    className="btn mt-4 fw-bold rounded-pill"
                    style={{
                      background: "linear-gradient(145deg, #43e97b, #38f9d7)",
                      color: "white",
                      boxShadow: "6px 6px 12px #c9c9c9, -6px -6px 12px #ffffff",
                    }}
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </motion.button>
                </form>
              )}
            </div>
          )}

          {/* Transactions */}
          {activeTab === "transactions" && (
            <div className="card shadow border-0 p-4 rounded-4">
              <h4 className="fw-bold text-secondary mb-3">
                Transactions History
              </h4>
              <input
                type="text"
                placeholder="Search..."
                className="form-control mb-3"
              />
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>1</td>
                    <td>
                      <span className="badge bg-success">Credit</span>
                    </td>
                    <td>₹1000</td>
                    <td>10 Sept 2025</td>
                  </tr>
                  <tr>
                    <td>2</td>
                    <td>
                      <span className="badge bg-danger">Debit</span>
                    </td>
                    <td>₹400</td>
                    <td>11 Sept 2025</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Bookings */}
          {activeTab === "bookings" && (
            <div className="card shadow border-0 p-4 rounded-4">
              <h4 className="fw-bold text-secondary mb-3">My Appointments</h4>
              <input
                type="text"
                placeholder="Search..."
                className="form-control mb-3"
                onChange={(e) => setSearch(e.target.value)}
              />
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Client</th>
                    <th>Date & Time</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings
                    .filter((b) =>
                      b.client.toLowerCase().includes(search.toLowerCase())
                    )
                    .map((b, index) => (
                      <tr key={b.id}>
                        <td>{index + 1}</td>
                        <td>{b.client}</td>
                        <td>{b.datetime}</td>
                        <td>
                          <span
                            className={`badge ${b.status === "Pending"
                              ? "bg-warning"
                              : b.status === "Accepted"
                                ? "bg-success"
                                : "bg-danger"
                              }`}
                          >
                            {b.status}
                          </span>
                        </td>
                        <td>
                          {b.status === "Pending" ? (
                            <>
                              <button
                                className="btn btn-success btn-sm me-2"
                              // onClick={() => handleAction(b.id, "Accepted")}
                              >
                                Accept
                              </button>
                              <button
                                className="btn btn-danger btn-sm"
                              // onClick={() => handleAction(b.id, "Rejected")}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <span>{b.status}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Messages */}
          {activeTab === "messages" && (
            <div className="card shadow border-0 p-4 rounded-4">
              <MessagesPanel
                initialChat={initialChat}
                userType="astrologer"   // optional, if MessagesPanel supports roles
              />
            </div>
          )}

          {/* Settings */}
          {activeTab === "settings" && (
            <div className="card shadow border-0 p-4 rounded-4">
              <h4 className="fw-bold text-secondary mb-3">
                Change/Reset Password
              </h4>
              <form>
                <div className="row gap-3">
                  <FormField
                    label="Current Password"
                    name="current_password"
                    value={user.current_password}
                    onChange={handleChange}
                    placeholder="Enter Your Current Password"
                    type="password"
                    showToggle={true}
                  />
                  <FormField
                    label="New Password"
                    name="new_password"
                    value={user.new_password}
                    onChange={handleChange}
                    placeholder="Enter Your New Password"
                    type="password"
                    showToggle={true}
                  />
                  <FormField
                    label="Confirm New Password"
                    name="new_password_confirmation"
                    value={user.new_password_confirmation}
                    onChange={handleChange}
                    placeholder="Enter Your Confirm New Password"
                    type="password"
                    showToggle={true}
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95, y: 2 }}
                  type="button"
                  disabled={loading}
                  onClick={handleChangePassword}
                  className="btn mt-4 fw-bold rounded-pill"
                  style={{
                    background: "linear-gradient(145deg, #43e97b, #38f9d7)",
                    color: "white",
                    boxShadow: "6px 6px 12px #c9c9c9, -6px -6px 12px #ffffff",
                  }}
                >
                  {loading ? "Saving..." : "Save Changes"}
                </motion.button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
